import { useTranslation } from "react-i18next";
import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { QuantityInput } from "@/components/ui/quantity-input";
import { AlertCircle, Trash2 } from "lucide-react";
import type { CustomPart, QuoteCustomPart } from "@onsemble/shared";
import {
  multiplyMoney,
  formatCentsAsDollars,
  formatUnitOfMeasure,
} from "@/lib/price-utils";

interface SelectedCustomPartItemProps {
  selectedCustomPart: QuoteCustomPart;
  customPart: CustomPart; // Full custom part details
  onQuantityChange: (quantity: number) => void;
  onRemove: () => void;
  showPricing?: boolean;
  disabled?: boolean;
  className?: string;
}

export const SelectedCustomPartItem: React.FC<SelectedCustomPartItemProps> = ({
  selectedCustomPart,
  customPart,
  onQuantityChange,
  onRemove,
  showPricing = false,
  disabled = false,
  className,
}) => {
  const { t } = useTranslation();
  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      onQuantityChange(newQuantity);
    },
    [onQuantityChange],
  );

  const handleRemove = useCallback(() => {
    onRemove();
  }, [onRemove]);

  // Get quantity from the QuoteCustomPart.quantity property
  const quantity = selectedCustomPart.quantity;
  const unitPrice = selectedCustomPart.customPrice || customPart.cost;
  const displayName = selectedCustomPart.customName || customPart.title;

  return (
    <div className={`p-3 border rounded-lg ${className || ""}`}>
      {/* Custom Part Info Row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0 pr-2">
          <h4 className="font-medium text-sm line-clamp-2 mb-1">
            {displayName}
          </h4>
          <div className="text-xs text-muted-foreground">{t("components.customParts.selectedCustomPartItem.customPart")}</div>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleRemove}
          disabled={disabled}
          className="text-muted-foreground hover:text-destructive p-1 h-6 w-6 flex-shrink-0"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      {/* Quantity and Price Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t("components.customParts.selectedCustomPartItem.qty")}</span>
          <QuantityInput
            value={quantity}
            onChange={handleQuantityChange}
            min={1}
            max={9999}
            size="sm"
            disabled={disabled}
          />
        </div>

        {showPricing && unitPrice ? (
          <div className="text-right">
            <div className="text-xs text-muted-foreground">
              {formatCentsAsDollars(unitPrice)}{" "}
              {formatUnitOfMeasure(null, true)}
            </div>
            <div className="text-sm font-semibold text-primary">
              {formatCentsAsDollars(multiplyMoney(unitPrice, quantity))}
            </div>
          </div>
        ) : showPricing ? (
          <div className="flex items-center gap-1 text-muted-foreground">
            <AlertCircle className="h-3 w-3" />
            <span className="text-xs">{t("components.customParts.selectedCustomPartItem.priceTbd")}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};
